import { Users, Home, GraduationCap, BookOpen, Heart, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { Statistic } from "@shared/schema";

interface StatisticsSectionProps {
  statistics?: Statistic[];
  isLoading: boolean;
}

const iconMap: Record<string, any> = {
  users: Users,
  home: Home,
  graduation: GraduationCap,
  book: BookOpen,
  heart: Heart,
  trending: TrendingUp,
};

export function StatisticsSection({ statistics, isLoading }: StatisticsSectionProps) {
  if (isLoading) {
    return (
      <section className="py-12" data-testid="section-statistics">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-64 mx-auto mb-2 rounded-xl neo-card-sm" />
          <Skeleton className="h-4 w-96 mx-auto rounded-xl neo-card-sm" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="p-6 rounded-2xl bg-card neo-card">
              <Skeleton className="w-12 h-12 rounded-xl neo-icon-box mb-4" />
              <Skeleton className="h-8 w-24 mb-2 rounded-xl neo-card-sm" />
              <Skeleton className="h-5 w-full mb-2 rounded-xl neo-card-sm" />
              <Skeleton className="h-4 w-full rounded-xl neo-card-sm" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!statistics || statistics.length === 0) {
    return null;
  }

  return (
    <section className="py-12" data-testid="section-statistics">
      <div className="text-center mb-8">
        <h2 className="font-poppins text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="heading-statistics">
          Village Statistics
        </h2>
        <p className="font-inter text-muted-foreground text-lg" data-testid="text-statistics-subtitle">
          Key demographics and community data
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statistics.map((stat) => {
          const Icon = iconMap[stat.icon] || Users;
          return (
            <div
              key={stat.id}
              className="p-6 rounded-2xl bg-card neo-card transition-all duration-300"
              data-testid={`card-statistic-${stat.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-background neo-icon-box">
                  <Icon className="w-6 h-6 text-primary" data-testid={`icon-${stat.icon}`} />
                </div>
                <div className="flex-1">
                  <div className="font-poppins text-3xl font-bold text-foreground mb-1" data-testid={`value-${stat.id}`}>
                    {stat.value}
                  </div>
                  <div className="font-inter text-base font-semibold text-foreground mb-1" data-testid={`label-${stat.id}`}>
                    {stat.label}
                  </div>
                  {stat.description && (
                    <div className="font-inter text-sm text-muted-foreground" data-testid={`description-${stat.id}`}>
                      {stat.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
